import { useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  MAPLIBRE_STYLE_URL,
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
} from "@/lib/map/config";
import type { UserLocation } from "@/lib/map/geolocation";

export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface ExploreMapHandle {
  flyTo: (lng: number, lat: number, zoom?: number) => void;
}

/** Minimal interface for map marker items */
export interface MapMarkerItem {
  id: string;
  lat: number | null;
  lng: number | null;
}

/** Extended marker with a display label */
interface MarkerWithLabel extends MapMarkerItem {
  title?: string;
  name?: string;
}

function getLabel(item: MarkerWithLabel): string {
  return item.title || item.name || "";
}

interface ExploreMapProps {
  items: MarkerWithLabel[];
  userLocation: UserLocation | null;
  selectedItemId: string | null;
  highlightedItemId?: string | null;
  previewOpen?: boolean;
  onItemSelect: (itemId: string | null) => void;
  onItemHover?: (itemId: string | null) => void;
  onBoundsChange?: (bounds: MapBounds) => void;
}

const LABEL_ZOOM_THRESHOLD = 10;
/** Cooldown (ms) after user interaction before auto-fit may trigger */
const USER_INTERACTION_COOLDOWN = 10_000;
/** Max zoom for auto-fit to avoid zooming into a single marker */
const AUTOFIT_MAX_ZOOM = 10;
/** Min zoom for auto-fit to avoid zooming out too far */
const AUTOFIT_MIN_ZOOM = 5;

const ExploreMap = forwardRef<ExploreMapHandle, ExploreMapProps>(({
  items,
  userLocation,
  selectedItemId,
  highlightedItemId,
  previewOpen = false,
  onItemSelect,
  onItemHover,
  onBoundsChange,
}, ref) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<Map<string, { marker: maplibregl.Marker; label: HTMLElement }>>(new Map());
  const userMarker = useRef<maplibregl.Marker | null>(null);
  const onBoundsChangeRef = useRef(onBoundsChange);
  onBoundsChangeRef.current = onBoundsChange;

  // ── User interaction tracking for auto-fit gating ──
  const lastUserInteraction = useRef<number>(0);
  const mapReady = useRef(false);
  // Track which item-set signature we last auto-fitted, to avoid re-fitting same data
  const lastAutoFitSignature = useRef<string>("");

  useImperativeHandle(ref, () => ({
    flyTo: (lng: number, lat: number, zoom?: number) => {
      map.current?.flyTo({ center: [lng, lat], zoom: zoom ?? 12, duration: 800 });
    },
  }));

  const emitBounds = useCallback(() => {
    if (!map.current || !onBoundsChangeRef.current) return;
    const b = map.current.getBounds();
    onBoundsChangeRef.current({
      north: b.getNorth(),
      south: b.getSouth(),
      east: b.getEast(),
      west: b.getWest(),
    });
  }, []);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const DEBUG = import.meta.env.VITE_EXPLORE_DEBUG === "true";
    const styleUrl = MAPLIBRE_STYLE_URL && MAPLIBRE_STYLE_URL.startsWith("http")
      ? MAPLIBRE_STYLE_URL
      : "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: styleUrl,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });

    if (DEBUG) {
      map.current.on("load", () => console.log("[MapLibre] load ok", { styleUrl }));
      map.current.on("error", (e: any) => console.error("[MapLibre] error", e?.error || e, { styleUrl }));
    }

    map.current.addControl(new maplibregl.NavigationControl(), "top-right");

    map.current.on("zoom", () => {
      updateLabelVisibility();
    });

    // Track user-initiated interactions
    const markUserInteraction = () => { lastUserInteraction.current = Date.now(); };
    map.current.on("dragstart", markUserInteraction);
    map.current.on("zoomstart", markUserInteraction);
    map.current.on("touchstart", markUserInteraction);

    map.current.on("moveend", emitBounds);
    map.current.on("load", () => {
      mapReady.current = true;
      emitBounds();
      requestAnimationFrame(() => {
        setTimeout(() => map.current?.resize(), 0);
      });
    });

    // ResizeObserver: watch map region for layout changes
    const regionEl = document.querySelector('[data-testid="explore-map-region"]') || mapContainer.current;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        setTimeout(() => map.current?.resize(), 0);
      });
    });
    ro.observe(regionEl);

    return () => {
      ro.disconnect();
      map.current?.remove();
      map.current = null;
      mapReady.current = false;
    };
  }, [emitBounds]);

  const updateLabelVisibility = useCallback(() => {
    if (!map.current) return;
    const zoom = map.current.getZoom();
    const showAllLabels = zoom >= LABEL_ZOOM_THRESHOLD;

    markers.current.forEach(({ label }, itemId) => {
      const isSelected = itemId === selectedItemId;
      const isHighlighted = itemId === highlightedItemId;
      const shouldShow = showAllLabels || isSelected || isHighlighted;
      label.style.display = shouldShow ? "block" : "none";

      if (isSelected) {
        label.className = "marker-label marker-label--selected";
      } else if (isHighlighted) {
        label.className = "marker-label marker-label--highlighted";
      } else {
        label.className = "marker-label";
      }
    });
  }, [selectedItemId, highlightedItemId]);

  useEffect(() => {
    updateLabelVisibility();
  }, [updateLabelVisibility]);

  // ── User location marker ──
  useEffect(() => {
    if (!map.current) return;
    if (userMarker.current) {
      userMarker.current.remove();
      userMarker.current = null;
    }
    if (userLocation) {
      const el = document.createElement("div");
      el.className = "user-location-marker";
      el.innerHTML = `
        <div class="user-location-pulse"></div>
        <div class="user-location-dot"></div>
      `;
      el.style.zIndex = "10";
      userMarker.current = new maplibregl.Marker({ element: el })
        .setLngLat([userLocation.lng, userLocation.lat])
        .addTo(map.current);
      // UX rule: No auto-pan on load — user centers manually via "Centrera på mig"
    }
  }, [userLocation]);

  const createMarkerElement = useCallback(
    (item: MarkerWithLabel, isSelected: boolean, isHighlighted: boolean) => {
      const el = document.createElement("div");
      el.className = "item-marker";
      el.style.cursor = "pointer";
      const isActive = isSelected || isHighlighted;
      const size = isActive ? 24 : 18;
      const color = isSelected ? "hsl(18,55%,55%)" : isHighlighted ? "hsl(145,30%,45%)" : "hsl(145,25%,38%)";
      const borderWidth = isActive ? 3 : 2;
      const shadow = isActive ? "0 3px 10px rgba(0,0,0,0.3)" : "0 2px 6px rgba(0,0,0,0.2)";
      el.innerHTML = `<div style="width:${size}px;height:${size}px;background:${color};border:${borderWidth}px solid white;border-radius:50%;box-shadow:${shadow};transition:all 0.2s ease"></div>`;
      return el;
    },
    []
  );

  const createLabelElement = useCallback((item: MarkerWithLabel) => {
    const label = document.createElement("div");
    label.className = "marker-label";
    label.textContent = getLabel(item);
    Object.assign(label.style, {
      position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)",
      marginTop: "4px", padding: "3px 8px", background: "white", borderRadius: "12px",
      fontSize: "11px", fontWeight: "500", color: "hsl(145,15%,25%)", whiteSpace: "nowrap",
      maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis",
      boxShadow: "0 1px 4px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)",
      pointerEvents: "auto", cursor: "pointer", zIndex: "1",
      textShadow: "0 0 2px rgba(255,255,255,0.8)",
    });
    return label;
  }, []);

  // ── Sync markers ──
  useEffect(() => {
    if (!map.current) return;
    const itemsWithCoords = items.filter((i) => i.lat != null && i.lng != null);
    const currentItemIds = new Set(itemsWithCoords.map((i) => i.id));

    markers.current.forEach(({ marker }, id) => {
      if (!currentItemIds.has(id)) {
        marker.remove();
        markers.current.delete(id);
      }
    });

    itemsWithCoords.forEach((item) => {
      const itemId = item.id;
      const isSelected = itemId === selectedItemId;
      const isHighlighted = itemId === highlightedItemId;
      const existingEntry = markers.current.get(itemId);

      if (existingEntry) {
        const markerEl = existingEntry.marker.getElement();
        const dotEl = markerEl.querySelector("div") as HTMLElement;
        if (dotEl) {
          const isActive = isSelected || isHighlighted;
          const size = isActive ? 24 : 18;
          const color = isSelected ? "hsl(18,55%,55%)" : isHighlighted ? "hsl(145,30%,45%)" : "hsl(145,25%,38%)";
          const borderWidth = isActive ? 3 : 2;
          const shadow = isActive ? "0 3px 10px rgba(0,0,0,0.3)" : "0 2px 6px rgba(0,0,0,0.2)";
          dotEl.style.width = `${size}px`;
          dotEl.style.height = `${size}px`;
          dotEl.style.background = color;
          dotEl.style.borderWidth = `${borderWidth}px`;
          dotEl.style.boxShadow = shadow;
        }
        const label = existingEntry.label;
        if (isSelected) {
          label.style.background = "hsl(18,55%,55%)";
          label.style.color = "white";
          label.style.fontWeight = "600";
        } else if (isHighlighted) {
          label.style.background = "hsl(145,30%,45%)";
          label.style.color = "white";
          label.style.fontWeight = "600";
        } else {
          label.style.background = "white";
          label.style.color = "hsl(145,15%,25%)";
          label.style.fontWeight = "500";
        }
      } else {
        const wrapper = document.createElement("div");
        wrapper.style.position = "relative";
        wrapper.style.display = "flex";
        wrapper.style.flexDirection = "column";
        wrapper.style.alignItems = "center";
        const markerEl = createMarkerElement(item, isSelected, isHighlighted);
        wrapper.appendChild(markerEl.cloneNode(true));
        const label = createLabelElement(item);
        wrapper.appendChild(label);
        wrapper.addEventListener("click", (e) => {
          e.stopPropagation();
          e.preventDefault();
          onItemSelect(itemId);
        });
        wrapper.addEventListener("mouseenter", () => { onItemHover?.(itemId); });
        wrapper.addEventListener("mouseleave", () => { onItemHover?.(null); });
        const marker = new maplibregl.Marker({ element: wrapper, anchor: "bottom" })
          .setLngLat([item.lng!, item.lat!])
          .addTo(map.current!);
        markers.current.set(itemId, { marker, label });
      }
    });

    updateLabelVisibility();
  }, [items, selectedItemId, highlightedItemId, onItemSelect, onItemHover, createMarkerElement, createLabelElement, updateLabelVisibility]);

  // ── Auto-fit to visible results ──
  // Triggers on initial load and when the result set changes.
  // Gated by: user interaction cooldown, preview not open, map ready.
  // Uses only items with valid coordinates when computing bounds.
  useEffect(() => {
    if (!map.current || !mapReady.current) return;
    if (previewOpen) return; // Don't auto-fit while preview is showing

    const itemsWithCoords = items.filter((i) => i.lat != null && i.lng != null);
    if (itemsWithCoords.length === 0) return;

    // Build a signature so we don't re-fit the same data set
    const sig = itemsWithCoords.map(i => i.id).sort().join(",");
    if (sig === lastAutoFitSignature.current) return;

    const isInitialFit = lastAutoFitSignature.current === "";

    // After the first fit, respect user interaction cooldown
    if (!isInitialFit) {
      const timeSinceInteraction = Date.now() - lastUserInteraction.current;
      if (timeSinceInteraction < USER_INTERACTION_COOLDOWN) return;

      // If results changed but some are already visible, skip refit
      const currentBounds = map.current.getBounds();
      const anyVisible = itemsWithCoords.some((i) =>
        currentBounds.contains([i.lng!, i.lat!])
      );
      if (anyVisible) {
        lastAutoFitSignature.current = sig;
        return;
      }
    }

    lastAutoFitSignature.current = sig;

    // Overlay-aware padding: extra bottom space for the rail/pill strip
    const overlayPadding = { top: 60, right: 60, bottom: 120, left: 60 };

    if (itemsWithCoords.length === 1) {
      // Single result → flyTo at a sensible zoom, capped by maxZoom
      const single = itemsWithCoords[0];
      map.current.flyTo({
        center: [single.lng!, single.lat!],
        zoom: Math.min(13, AUTOFIT_MAX_ZOOM),
        duration: isInitialFit ? 0 : 1000,
      });
    } else {
      // 2+ results → fitBounds to exactly frame visible results
      const fitBounds = new maplibregl.LngLatBounds();
      itemsWithCoords.forEach((i) => fitBounds.extend([i.lng!, i.lat!]));
      map.current.fitBounds(fitBounds, {
        padding: overlayPadding,
        maxZoom: AUTOFIT_MAX_ZOOM,
        duration: isInitialFit ? 0 : 1000,
      });
    }
  }, [items, previewOpen]);

  // ── Background click deselect ──
  useEffect(() => {
    if (!map.current) return;
    const handleClick = () => { onItemSelect(null); };
    map.current.on("click", handleClick);
    return () => { map.current?.off("click", handleClick); };
  }, [onItemSelect]);

  return <div ref={mapContainer} className="w-full h-full" />;
});

ExploreMap.displayName = "ExploreMap";

export default ExploreMap;
